/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState, useMemo } from 'react';
import {
  Modal, Button, Alert, Spinner, Badge, Card, Form,
} from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import Error from '../../../Components/Error';
import { formatBookingDateOnly, formatBookingTimeOnly } from '../../../Utils/timezone';
import BookingService from '../../../Services/BookingService';
import OperatorService from '../../../Services/OperatorService';

function BatchDriverInputModal({
  bookings: bookingsProp = [],
  show = false,
  handleClose,
  fetchOrders,
}) {
  // Ensure bookings is always an array - use useMemo to stabilize the reference
  const bookings = useMemo(
    () => (Array.isArray(bookingsProp) ? bookingsProp : []),
    [bookingsProp],
  );

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Drivers and cars per operator: { operatorId: { drivers: [], cars: [] } }
  const [operatorData, setOperatorData] = useState({});

  // Per-booking assignments: { bookingId: { driver: [], car: [] } }
  const [assignments, setAssignments] = useState({});

  // Track failed bookings: { bookingId: errorMessage }
  const [failedBookings, setFailedBookings] = useState({});

  const decorateDriverOption = (option) => {
    if (!option) return null;
    return {
      id: option.Id || option.id || option._id,
      label: `${option.full_name} - ${option.phone_number}`,
    };
  };

  const decorateCarOption = (option) => {
    if (!option) return null;
    return {
      id: option.Id || option.id || option._id,
      label: `${option.plate_number || ''} - ${option.type || ''} - ${option.color || ''}`,
    };
  };

  // Fetch drivers and cars for a specific operator
  const fetchOperatorData = async (operatorId) => {
    if (!operatorId || operatorData[operatorId]) return;

    try {
      const [driversResponse, carsResponse] = await Promise.all([
        OperatorService.fetchDrivers(operatorId),
        OperatorService.fetchCars(operatorId),
      ]);

      const driversData = driversResponse?.data?.data || [];
      const carsData = carsResponse?.data?.data || [];

      setOperatorData((prev) => ({
        ...prev,
        [operatorId]: {
          drivers: driversData.map((d) => decorateDriverOption(d)),
          cars: carsData.map((c) => decorateCarOption(c)),
        },
      }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`Failed to load data for operator ${operatorId}:`, err);
    }
  };

  // Get unique operator IDs from bookings
  const uniqueOperatorIds = [...new Set(bookings.map((b) => b.operator_id).filter(Boolean))];

  useEffect(() => {
    if (show) {
      setError('');
      setFailedBookings({});
      setLoading(true);

      // Initialize assignments with existing driver/car data
      const initialAssignments = {};
      bookings.forEach((booking) => {
        initialAssignments[booking._id] = {
          driver: booking.driver ? [decorateDriverOption(booking.driver)] : [],
          car: booking.car ? [decorateCarOption(booking.car)] : [],
        };
      });
      setAssignments(initialAssignments);

      // Fetch drivers/cars for all unique operators
      Promise.all(uniqueOperatorIds.map((id) => fetchOperatorData(id)))
        .finally(() => setLoading(false));
    }
  }, [show]);

  const updateAssignment = (bookingId, field, value) => {
    setAssignments((prev) => ({
      ...prev,
      [bookingId]: {
        ...prev[bookingId],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    // Validate all bookings with operators have driver and car assigned
    const bookingsWithOperators = bookings.filter((b) => b.operator_id);
    const missingAssignments = bookingsWithOperators.filter(
      (b) => !assignments[b._id]?.driver?.length || !assignments[b._id]?.car?.length,
    );

    if (missingAssignments.length > 0) {
      setError(`Please assign driver and car to all bookings (${missingAssignments.length} incomplete)`);
      return;
    }

    setSubmitting(true);
    setFailedBookings({});
    try {
      // Build assignments array with each booking's specific driver/car details
      const assignmentsArray = bookingsWithOperators.map((booking) => {
        const assignment = assignments[booking._id];
        return {
          booking_id: booking._id,
          operator_driver_id: assignment.driver[0].id,
          operator_car_id: assignment.car[0].id,
        };
      });

      // Single API call with all assignments
      const result = await BookingService.bulkAssignDriver(assignmentsArray);

      // Check for partial failures
      if (result?.errors?.length > 0) {
        const failed = {};
        result.errors.forEach((err) => {
          failed[err.bookingId] = err.error;
        });
        setFailedBookings(failed);
        setError(`${result.updatedCount} succeeded, ${result.errors.length} failed. See highlighted rows below.`);
        fetchOrders();
      } else {
        // All succeeded
        fetchOrders();
        handleClose();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign drivers');
    } finally {
      setSubmitting(false);
    }
  };

  // Apply same driver/car to all bookings with the same operator
  const applyToSameOperator = (bookingId) => {
    const sourceBooking = bookings.find((b) => b._id === bookingId);
    const sourceAssignment = assignments[bookingId];
    if (!sourceBooking?.operator_id || !sourceAssignment?.driver?.length) return;

    setAssignments((prev) => {
      const updated = { ...prev };
      bookings
        .filter((b) => b.operator_id === sourceBooking.operator_id)
        .forEach((booking) => {
          updated[booking._id] = { ...sourceAssignment };
        });
      return updated;
    });
  };

  // Check if any bookings are missing operators
  const bookingsWithoutOperator = bookings.filter((b) => !b.operator_id);

  return (
    <Modal show={show} onHide={handleClose} size="lg" fullscreen="md-down">
      <Modal.Header closeButton>
        <Modal.Title>
          Batch Assign Drivers & Cars
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="mb-3">
          <i className="fa fa-info-circle me-2" />
          Assign drivers and cars to
          {' '}
          <Badge bg="primary">{bookings.length}</Badge>
          {' '}
          booking(s).
        </Alert>

        {bookingsWithoutOperator.length > 0 && (
          <Alert variant="warning" className="mb-3">
            <i className="fa fa-exclamation-triangle me-2" />
            {bookingsWithoutOperator.length}
            {' '}
            booking(s) have no operator and will be skipped.
          </Alert>
        )}

        <Error error={error} setError={setError} />

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-2">Loading drivers and cars...</p>
          </div>
        ) : (
          <div>
            {bookings.map((booking) => {
              const opData = operatorData[booking.operator_id] || { drivers: [], cars: [] };
              const hasOperator = !!booking.operator_id;

              return (
                <Card
                  key={booking._id}
                  className={`mb-2 ${failedBookings[booking._id] ? 'border-danger' : ''} ${!hasOperator ? 'bg-light' : ''}`}
                >
                  <Card.Body className="py-2 px-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <div className="fw-bold">{booking.full_name || 'Unknown'}</div>
                        <small className="text-muted">
                          {booking.date_datetime
                            ? `${formatBookingDateOnly(booking, 'date_datetime')} ${formatBookingTimeOnly(booking, 'date_datetime')}`
                            : '-'}
                        </small>
                        {hasOperator ? (
                          <div className="text-success small">
                            <i className="fa fa-building me-1" />
                            {booking.operator?.name || 'Unknown'}
                          </div>
                        ) : (
                          <div className="text-danger small">
                            <i className="fa fa-exclamation-triangle me-1" />
                            No operator
                          </div>
                        )}
                      </div>
                      {hasOperator && assignments[booking._id]?.driver?.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          title="Apply to all with same operator"
                          onClick={() => applyToSameOperator(booking._id)}
                        >
                          <i className="fa fa-copy" />
                        </Button>
                      )}
                    </div>
                    {failedBookings[booking._id] && (
                      <div className="text-danger small mb-2">
                        <i className="fa fa-exclamation-circle me-1" />
                        {failedBookings[booking._id]}
                      </div>
                    )}
                    {hasOperator && (
                      <>
                        <Form.Group className="mb-2">
                          <Form.Label className="small mb-1">Driver</Form.Label>
                          <Typeahead
                            size="sm"
                            placeholder="Select driver"
                            id={`driver_${booking._id}`}
                            onChange={(selected) => updateAssignment(booking._id, 'driver', selected)}
                            options={opData.drivers}
                            selected={assignments[booking._id]?.driver || []}
                            positionFixed
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label className="small mb-1">Car</Form.Label>
                          <Typeahead
                            size="sm"
                            placeholder="Select car"
                            id={`car_${booking._id}`}
                            onChange={(selected) => updateAssignment(booking._id, 'car', selected)}
                            options={opData.cars}
                            selected={assignments[booking._id]?.car || []}
                            positionFixed
                          />
                        </Form.Group>
                      </>
                    )}
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="flex-shrink-0">
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || loading || bookings.every((b) => !b.operator_id)}
        >
          {submitting ? (
            <>
              <Spinner size="sm" className="me-2" />
              Assigning...
            </>
          ) : (
            <>
              <i className="fa fa-car me-2" />
              Assign Drivers & Cars
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default BatchDriverInputModal;
